import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

export type RevisionEntityType =
  | 'site_setting'
  | 'homepage_section'
  | 'page'
  | 'homepage_order';

export type RevisionReason =
  | 'edit'
  | 'publish'
  | 'restore'
  | 'delete'
  | 'brand_kit'
  | 'ai'
  | 'import';

export interface ContentRevision {
  id: string;
  entity_type: RevisionEntityType;
  entity_id: string;
  reason: RevisionReason;
  label: string | null;
  restore_point_id: string | null;
  created_by: string | null;
  created_at: string;
  /** Only present when a single revision is fetched by id. */
  snapshot?: unknown;
}

export interface RestorePoint {
  id: string;
  label: string;
  kind: 'auto' | 'manual' | 'pre_publish' | 'pre_restore';
  created_by: string | null;
  created_at: string;
  snapshot?: unknown;
}

export const REVISIONS_KEY = ['content-revisions'] as const;
export const RESTORE_POINTS_KEY = ['content-restore-points'] as const;

/**
 * After any restore the live site has changed underneath every cached query —
 * settings, homepage sections and pages all at once — so the safest thing is to
 * drop the lot rather than try to enumerate what moved.
 */
function invalidateEverythingDesign(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: REVISIONS_KEY });
  qc.invalidateQueries({ queryKey: RESTORE_POINTS_KEY });
  qc.invalidateQueries({ queryKey: ['settings'] });
  qc.invalidateQueries({ queryKey: ['homepage'] });
  qc.invalidateQueries({ queryKey: ['pages'] });
}

export function useRevisions(entityType: RevisionEntityType, entityId: string, enabled = true) {
  return useQuery({
    queryKey: [...REVISIONS_KEY, entityType, entityId],
    enabled: enabled && !!entityId,
    queryFn: () =>
      api
        .get<{ revisions: ContentRevision[] }>(
          `/admin/revisions?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`,
        )
        .then((r) => r.revisions),
  });
}

export function useRestorePoints() {
  return useQuery({
    queryKey: RESTORE_POINTS_KEY,
    queryFn: () =>
      api.get<{ restore_points: RestorePoint[] }>('/admin/restore-points').then((r) => r.restore_points),
  });
}

export function useCreateRestorePoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => api.post<{ id: string }>('/admin/restore-points', { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RESTORE_POINTS_KEY }),
  });
}

export function useRestoreToPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ success: boolean; undo_restore_point_id: string }>(
        `/admin/restore-points/${id}/restore`,
        {},
      ),
    onSuccess: () => invalidateEverythingDesign(qc),
  });
}

export function useRestoreRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/admin/revisions/${id}/restore`, {}),
    onSuccess: () => invalidateEverythingDesign(qc),
  });
}
